import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { showSuccess, showError } from '../utils/notifications';
import { Check, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import {
  getProfilePasswordChecks,
  profileNewPasswordMeetsPolicy,
  type ProfilePasswordRuleCheck
} from '../utils/profilePasswordPolicy';
import './ProfilePage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';

/** Intentos fallidos de contraseña actual en esta pestaña (memoria); al 3.º se cierra sesión. */
const MAX_PROFILE_PASSWORD_FAILS = 3;
let profileWrongPasswordAttempts = 0;

function isWrongCurrentPasswordError(err: any): boolean {
  const code = err.response?.data?.error?.details?.code;
  if (code === 'WRONG_CURRENT_PASSWORD') return true;
  const msg = String(err.response?.data?.error?.message || '').toLowerCase();
  return (
    err.response?.status === 400 &&
    (msg.includes('contraseña actual') || msg.includes('current password'))
  );
}

const ProfilePage: React.FC = () => {
  const { user, updateUser, logout } = useAuth();
  const [email, setEmail] = useState(user?.email || '');
  const [username, setUsername] = useState(user?.username || '');
  const [nationalId, setNationalId] = useState(user?.nationalId || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newPasswordFieldError, setNewPasswordFieldError] = useState<string | null>(null);

  const newPasswordChecks = useMemo(() => getProfilePasswordChecks(newPassword), [newPassword]);
  const showNewPasswordChecklist = newPassword.length > 0;

  useEffect(() => {
    if (!user) return;
    setEmail(user.email);
    setUsername(user.username || '');
    setNationalId(user.nationalId || '');
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword) {
      showError('Debes ingresar tu contraseña actual para realizar cambios');
      return;
    }

    if (newPassword.trim()) {
      if (!profileNewPasswordMeetsPolicy(newPassword)) {
        setNewPasswordFieldError(
          'La nueva contraseña no cumple todos los requisitos. Revisa la lista inferior.'
        );
        return;
      }
      if (newPassword !== confirmNewPassword) {
        setNewPasswordFieldError('La confirmación no coincide con la nueva contraseña.');
        return;
      }
    }
    setNewPasswordFieldError(null);

    setLoading(true);
    try {
      const response = await axios.patch(`${API_URL}/auth/profile`, {
        currentPassword,
        email: email.trim(),
        username: username.trim(),
        nationalId: nationalId.trim() || null,
        ...(newPassword.trim() ? { newPassword: newPassword.trim() } : {})
      });

      const updated = response.data?.data;
      if (updated) {
        profileWrongPasswordAttempts = 0;
        updateUser({
          email: updated.email,
          username: updated.username ?? null,
          nationalId: updated.nationalId ?? null,
        });
        showSuccess('Perfil actualizado correctamente');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
        setNewPasswordFieldError(null);
      }
    } catch (err: any) {
      if (isWrongCurrentPasswordError(err)) {
        profileWrongPasswordAttempts += 1;
        const left = MAX_PROFILE_PASSWORD_FAILS - profileWrongPasswordAttempts;
        if (profileWrongPasswordAttempts >= MAX_PROFILE_PASSWORD_FAILS) {
          profileWrongPasswordAttempts = 0;
          showError(
            'Demasiados intentos fallidos con la contraseña actual. Por seguridad se cerrará la sesión.'
          );
          logout();
          return;
        }
        showError(
          left > 0
            ? `La contraseña actual no es correcta. Te quedan ${left} intento${left === 1 ? '' : 's'}.`
            : 'La contraseña actual no es correcta.'
        );
      } else {
        const msg =
          err.response?.data?.error?.message ||
          err.response?.data?.message ||
          err.message ||
          'Error al actualizar el perfil';
        showError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-header">
        <h1>Mi perfil</h1>
        <p>Actualiza tu información personal. Siempre se requiere tu contraseña actual por seguridad.</p>
      </div>

      <form onSubmit={handleSubmit} className="profile-form">
        <div className="profile-form-grid">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="username">Nombre</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Tu nombre"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="nationalId">Cédula o documento</label>
            <input
              id="nationalId"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={nationalId}
              onChange={(e) => setNationalId(e.target.value)}
              placeholder="Para constancias y certificados de descarga"
              disabled={loading}
            />
          </div>

          <div className="form-group profile-form-col-1">
            <label htmlFor="currentPassword">Contraseña actual *</label>
            <div className="password-input-wrapper">
              <input
                id="currentPassword"
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Requerida para cualquier cambio"
                required
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                tabIndex={-1}
                aria-label={showCurrentPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
              >
                {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          <div className="profile-form-empty-cell" aria-hidden="true" />

          <div className="form-group profile-new-password-block profile-form-col-1">
            <label htmlFor="newPassword">Nueva contraseña (opcional)</label>
            <div className="password-input-wrapper">
              <input
                id="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => {
                  const v = e.target.value;
                  setNewPassword(v);
                  if (!v) setConfirmNewPassword('');
                  if (newPasswordFieldError) setNewPasswordFieldError(null);
                }}
                placeholder="Dejar vacío para no cambiar"
                disabled={loading}
                className={newPasswordFieldError ? 'profile-input--error' : undefined}
                aria-invalid={Boolean(newPasswordFieldError)}
                aria-describedby={
                  [
                    showNewPasswordChecklist ? 'profile-password-checklist' : '',
                    newPasswordFieldError ? 'profile-password-error' : ''
                  ]
                    .filter(Boolean)
                    .join(' ') || undefined
                }
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowNewPassword(!showNewPassword)}
                tabIndex={-1}
                aria-label={showNewPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
              >
                {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {showNewPasswordChecklist && (
            <div className="form-group profile-confirm-password-group">
              <label htmlFor="confirmNewPassword">Confirmar contraseña</label>
              <div className="password-input-wrapper">
                <input
                  id="confirmNewPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmNewPassword}
                  onChange={(e) => {
                    setConfirmNewPassword(e.target.value);
                    if (newPasswordFieldError) setNewPasswordFieldError(null);
                  }}
                  placeholder="Repite la nueva contraseña"
                  disabled={loading}
                  autoComplete="new-password"
                  className={newPasswordFieldError ? 'profile-input--error' : undefined}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                  aria-label={showConfirmPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
          )}

          {showNewPasswordChecklist && (
            <div
              id="profile-password-checklist"
              className="profile-password-checklist profile-form-span-2"
              aria-live="polite"
            >
              <p className="profile-password-checklist-title">
                La nueva contraseña debe cumplir con:
              </p>
              <ul className="profile-password-checklist-items">
                {newPasswordChecks.map((rule: ProfilePasswordRuleCheck) => (
                  <li
                    key={rule.id}
                    className={
                      rule.ok
                        ? 'profile-password-rule profile-password-rule--met'
                        : 'profile-password-rule'
                    }
                  >
                    <span className="profile-password-rule-icon" aria-hidden>
                      {rule.ok ? (
                        <Check
                          className="profile-password-check-icon"
                          size={16}
                          strokeWidth={2.5}
                        />
                      ) : (
                        <span className="profile-password-rule-dash">—</span>
                      )}
                    </span>
                    <span className="profile-password-rule-text">{rule.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {newPasswordFieldError && (
            <p
              id="profile-password-error"
              className="profile-password-field-error profile-form-span-2"
              role="alert"
            >
              {newPasswordFieldError}
            </p>
          )}

          <div className="profile-form-footer">
            <button type="submit" className="profile-submit-btn" disabled={loading}>
              {loading ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProfilePage;
